import React, { useState, useEffect, useCallback } from 'react';
import { getPreservicios } from '../../api/preservicios';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent, TextField, CircularProgress, Box, Chip, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Add, Visibility, Search } from '@mui/icons-material';
import CreatePreservicio from './_Create';
import ViewPreservicio from './_ViewById';
import { useAuth } from '../../context/auth';
import { AccessDenied } from '../../components/AccessDenied/Index';

const ESTADOS: Record<number, { label: string; color: string }> = {
  1: { label: 'Pendiente', color: '#f5a623' },
  2: { label: 'En revisión', color: '#4a90d9' },
  3: { label: 'Servicio Asignado', color: '#28a745' },
  4: { label: 'Rechazado/Cancelado', color: '#d9534f' },
};

const Preservicios = () => {
  const [preservicios, setPreservicios] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { permisos, user } = useAuth();
  const { Prm_Preservicio_Ver, Prm_Preservicio_Crear } = permisos;
  const esCliente = !!user?.Us_Cl_Id;

  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [clienteODocumento, setClienteODocumento] = useState('');

  const fetchPreservicios = useCallback(async () => {
    setLoading(true);
    const response = await getPreservicios({
      fechaInicio: fechaInicio || undefined,
      fechaFin: fechaFin || undefined,
      estado: estadoFiltro || undefined,
      clienteODocumento: !esCliente && clienteODocumento ? clienteODocumento : undefined,
    });
    if (response.isOk) {
      setPreservicios(response.data);
    }
    setLoading(false);
  }, [fechaInicio, fechaFin, estadoFiltro, clienteODocumento, esCliente]);

  useEffect(() => {
    fetchPreservicios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Igual que en el resto del sistema: la pantalla se autoprotege por permiso, no solo se
  // oculta del menu. Un cliente siempre tiene Prm_Preservicio_Ver=1 en su perfil.
  if (Prm_Preservicio_Ver !== 1) {
    return <AccessDenied />;
  }

  return (
    <div style={{ marginTop: '5px' }}>
      {loading ? (
        <Box display='flex' justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ marginTop: '5px' }}>
          <Grid container spacing={2} alignItems='center' mb={2}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField label='Fecha Inicio' type='date' value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size='small' />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField label='Fecha Fin' type='date' value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size='small' />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl variant='outlined' size='small' fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value as string)} label='Estado'>
                  <MenuItem value=''>
                    <em>Todos</em>
                  </MenuItem>
                  {Object.entries(ESTADOS).map(([idEstado, { label }]) => (
                    <MenuItem key={idEstado} value={idEstado}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {!esCliente && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField label='Cliente o documento' value={clienteODocumento} onChange={(e) => setClienteODocumento(e.target.value)} fullWidth size='small' />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={esCliente ? 5 : 2}>
              <Box display='flex' justifyContent='flex-end'>
                <IconButton onClick={fetchPreservicios} title='Buscar' className='tool-btn'>
                  <Search />
                </IconButton>
                {esCliente && Prm_Preservicio_Crear === 1 && (
                  <IconButton onClick={() => setOpenCreate(true)} title='Nuevo preservicio' className='tool-btn'>
                    <Add color='success' />
                  </IconButton>
                )}
              </Box>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  {!esCliente && <TableCell>Cliente</TableCell>}
                  <TableCell>Fecha</TableCell>
                  <TableCell>Vehículo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preservicios.map((p: any) => (
                  <TableRow key={p.Psrv_Id}>
                    <TableCell>{p.Psrv_Id}</TableCell>
                    {!esCliente && <TableCell>{p.Cl_Nombre}</TableCell>}
                    <TableCell>{p.Psrv_FechaCreacion}</TableCell>
                    <TableCell>
                      {p.Psrv_VehiculoMarca ? `${p.Psrv_VehiculoMarca} ${p.Psrv_VehiculoReferencia ?? ''} ${p.Psrv_VehiculoPlacaOSerial ?? ''}` : 'Por definir'}
                    </TableCell>
                    <TableCell>
                      <Chip label={ESTADOS[p.Psrv_Estado]?.label ?? 'Desconocido'} size='small' sx={{ backgroundColor: ESTADOS[p.Psrv_Estado]?.color, color: 'white' }} />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedId(p.Psrv_Id);
                          setOpenView(true);
                        }}
                        title='Ver'>
                        <Visibility color='info' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {preservicios.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={esCliente ? 5 : 6} align='center'>
                      No hay preservicios registrados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}

      {openCreate && (
        <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth='sm'>
          <DialogTitle>Nuevo preservicio</DialogTitle>
          <DialogContent>
            <CreatePreservicio
              onClose={() => {
                setOpenCreate(false);
                fetchPreservicios();
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {openView && selectedId !== null && (
        <Dialog open={openView} onClose={() => setOpenView(false)} fullWidth maxWidth='md'>
          <DialogTitle>Preservicio #{selectedId}</DialogTitle>
          <DialogContent>
            <ViewPreservicio
              id={selectedId}
              onClose={() => {
                setOpenView(false);
                fetchPreservicios();
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Preservicios;
